import type { StateStorage } from 'zustand/middleware'

/**
 * Where persisted zustand stores keep their state.
 *
 * The web reads `localStorage`; Metro resolves `platform-storage.native.ts`
 * instead and reads AsyncStorage. Naming the storage explicitly rather than
 * letting zustand default to `localStorage` is what lets the same store file
 * run on both clients — the default is a browser global that does not exist on
 * a phone, and a store reaching for it there fails at import.
 *
 * The interface is deliberately the async-capable one, because AsyncStorage is
 * async and `createJSONStorage` accepts either.
 */
export const platformStorage: StateStorage = {
  getItem: (name) => {
    // Guarded because this module is imported during module evaluation, which
    // on a server render happens before any browser global exists.
    if (typeof window === 'undefined') return null
    try {
      return window.localStorage.getItem(name)
    } catch {
      // Private browsing and blocked storage both throw rather than return null.
      return null
    }
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(name, value)
    } catch {
      // Over quota, or storage disabled. Losing persistence is survivable;
      // throwing out of a store write is not.
    }
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.removeItem(name)
    } catch {
      // As above.
    }
  },
}
